import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Collapse, Row, Col } from 'antd';
import { StyleUtils } from '../../misc';
import { GetComponentRowsFunction } from '../../componentTypes';
import { SectionModel, SectionsNodeModel } from '../../models';
import { CollapseExpandIcon } from '../../../custom_shared/components';
import SectionHeader from './SectionHeader';
import './SectionRenderer.less';

interface Props {
    node: SectionsNodeModel;
    section: SectionModel;
    getComponentRows: GetComponentRowsFunction;
    addSectionRef: (sectionId: string, sectionRef: React.RefObject<HTMLDivElement>) => void;
}

const SectionRenderer: React.FC<Props> = (props: Props) => {
    const { node, section, getComponentRows, addSectionRef } = props;

    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        addSectionRef(section.id, ref);
    }, [section.id, addSectionRef]);

    const rows = React.useMemo(
        () => getComponentRows(section.inputs, { NodeId: node.id, SectionId: section.id }),
        [section.inputs, node.id, section.id, getComponentRows]
    );

    const getClassName = () => {
        let className = 'sections-layout-section-renderer-container';

        if (section.isDisabled) {
            className += ' disabled';
        }

        return className;
    };

    return (
        <div ref={ref} className={getClassName()}>
            <Collapse
                key={section.id}
                activeKey={section.expanded ? [section.id] : []}
                onChange={section.toggleExpanded}
                bordered={false}
                expandIcon={CollapseExpandIcon}
            >
                <Collapse.Panel
                    className="section-collapse-panel"
                    header={<SectionHeader section={section} />}
                    key={section.id}
                >
                    {rows.map((componentRow, index) => (
                        <Row key={index} gutter={10}>
                            {componentRow.map(({ component, columnSpan }) => (
                                <Col key={component.key} style={StyleUtils.getNodeColumnStyle(columnSpan ?? 8)}>
                                    {component}
                                </Col>
                            ))}
                        </Row>
                    ))}
                </Collapse.Panel>
            </Collapse>
        </div>
    );
};

export default observer(SectionRenderer);
